import { useEffect, useMemo, useState } from 'react';
import { Card, CardType } from '../types/card';
import { DROP_STATUSES } from '../constants/constants';
import { saleStatusLabel, SALE_STATUS_ORDER } from '../components/common/AppTag';
import { useListSelection } from './UseListSelection';

// ─── 타입 ─────────────────────────────────────────────────────────────────────

export type QuickFilter = 'ALL' | 'DROP' | 'INCREASE';

export interface FilterOption {
  value: string;
  label: string;
  /** 확장팩 옵션 전용 — 카드 수(2026-08-27, ExpansionFilterSelect). 레어도 옵션 등에선 비워둔다. */
  count?: number | null;
  /** 확장팩 옵션 전용 — pricehub 카탈로그 발매일(UseExpansionCatalog가 채움, 로컬 파생 목록엔 없음). */
  releaseDate?: string | null;
  /** 확장팩 옵션 전용 — pricehub productType(부스터팩/스타터덱). */
  productType?: 'BOOSTER' | 'STARTER';
  /** 상품 등록 드롭다운 전용 — 로컬에 이미 등록된 건수(2026-08-31). 있으면 각 행에
   *  "미등록 N장 / 전부 등록됨" 배지를 보여준다(count와 함께 있을 때만). */
  registeredCount?: number | null;
}

// ─── useCardFilters ───────────────────────────────────────────────────────────

// 가격 탭(useCardView)과 재고 탭(StockPageContext)이 각자 독립된 인스턴스로 호출해서 쓴다 —
// 검색/필터 상태를 탭끼리 공유하지 않기 위함(재고는 가격과 무관, StockPageContext 참고).
export function useCardFilters(cards: Card[]) {
  const [search, setSearch]           = useState('');
  const [typeFilter, setTypeFilter]   = useState<CardType | 'ALL'>('ALL');
  const [expansionFilter, setExpansionFilter] = useState<string>('ALL');
  // 레어도는 체크박스 다중 선택 — 빈 배열은 "전체"(필터 없음)를 의미한다.
  const [rarityFilter, setRarityFilter]       = useState<string[]>([]);
  const [saleStatusFilter, setSaleStatusFilter] = useState<string>('ALL');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('ALL');
  // "코드 확인 필요만 보기" — ImportProductsJob이 판매자상품코드 중복을 자동으로 임시
  // 코드 부여·Naver 반영까지 마쳤어도, 그 임시 코드는 사람이 알아보기 쉬운 값이 아니라서
  // 나중에 확인이 필요한 카드만 보는 필터(2026-08-20). 종류/확장팩과 달리 typeFilter가
  // 바뀌어도 초기화하지 않는다 — 코드 확인 작업 중 종류를 바꿔 봐도 되는 별개의 관심사이기 때문.
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);

  // 종류(typeFilter)를 바꾸면 그 종류엔 없는 확장팩/레어도가 선택돼 있을 수 있어 초기화한다.
  useEffect(() => {
    setExpansionFilter('ALL');
    setRarityFilter([]);
  }, [typeFilter]);

  // 확장팩/레어도 드롭다운 옵션 — 고정 목록이 아니라 실제 등록된 카드에서 종류(typeFilter)
  // 기준으로 동적으로 뽑는다(확장팩은 게임마다 계속 늘어나서 CARD_TYPE처럼 enum으로 못 둔다).
  const cardsInType = useMemo(
    () => (typeFilter === 'ALL' ? cards : cards.filter((c) => c.cardType === typeFilter)),
    [cards, typeFilter],
  );

  const expansionOptions = useMemo<FilterOption[]>(() => {
    const byCode = new Map<string, { label: string; count: number }>();
    for (const c of cardsInType) {
      if (!c.expansionCode) continue;
      const cur = byCode.get(c.expansionCode);
      if (cur) cur.count += 1;
      else byCode.set(c.expansionCode, { label: c.expansionName ?? c.expansionCode, count: 1 });
    }
    return Array.from(byCode, ([value, { label, count }]) => ({ value, label, count }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ko'));
  }, [cardsInType]);

  const rarityOptions = useMemo<FilterOption[]>(() => {
    const rarities = new Set<string>();
    for (const c of cardsInType) {
      if (c.rarity) rarities.add(c.rarity);
    }
    return Array.from(rarities).sort((a, b) => a.localeCompare(b, 'ko'))
      .map((value) => ({ value, label: value }));
  }, [cardsInType]);

  // 판매상태는 종류(typeFilter)와 무관 — 스토어 미등록(DELETED) 등을 찾아 정리하는 용도라
  // 카드 종류 필터에 걸러지지 않고 항상 전체 카드 기준으로 옵션을 뽑는다.
  const { saleStatusOptions, saleStatusCounts } = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of cards) {
      if (c.naverSaleStatus) counts[c.naverSaleStatus] = (counts[c.naverSaleStatus] ?? 0) + 1;
    }
    // 네이버 자체 상품관리 화면처럼 0건인 상태도 항상 노출한다 — 현재 데이터에 존재하는
    // 상태만 보여주면 승인대기/판매종료처럼 아직 카드가 없는 상태가 안 보여 혼란을 준다.
    // SALE_STATUS_ORDER에 없는 값(예상 못한 새 상태)은 뒤에 이어 붙여 놓치지 않는다.
    const extra = Object.keys(counts)
      .filter((value) => !SALE_STATUS_ORDER.includes(value))
      .sort((a, b) => a.localeCompare(b, 'ko'));
    const options = [...SALE_STATUS_ORDER, ...extra]
      .map((value) => ({ value, label: saleStatusLabel(value) }));
    return { saleStatusOptions: options, saleStatusCounts: counts };
  }, [cards]);

  // 필터와 무관하게 "지금 전체 카드 중 코드 확인이 필요한 대상이 몇 건인지"를 항상 보여주기
  // 위한 카운트 — saleStatusCounts와 같은 이유로 typeFilter 등에 걸러지지 않는다.
  const needsReviewCount = useMemo(
    () => cards.filter((c) => c.sellerCodeNeedsReview).length,
    [cards],
  );

  const { filtered, dropCount, increasedCount } = useMemo(() => {
    let drop = 0;
    let increased = 0;
    const result: Card[] = [];

    for (const c of cards) {
      if (DROP_STATUSES.includes(c.priceChangeStatus)) drop++;
      if (c.priceChangeStatus === 'INCREASED') increased++;

      if (typeFilter !== 'ALL' && c.cardType !== typeFilter) continue;
      if (expansionFilter !== 'ALL' && c.expansionCode !== expansionFilter) continue;
      if (rarityFilter.length > 0 && (c.rarity === null || !rarityFilter.includes(c.rarity))) continue;
      if (saleStatusFilter !== 'ALL' && c.naverSaleStatus !== saleStatusFilter) continue;
      if (!c.productName.includes(search)) continue;
      if (quickFilter === 'DROP' && !DROP_STATUSES.includes(c.priceChangeStatus)) continue;
      if (quickFilter === 'INCREASE' && c.priceChangeStatus !== 'INCREASED') continue;
      if (needsReviewOnly && !c.sellerCodeNeedsReview) continue;

      result.push(c);
    }

    return { filtered: result, dropCount: drop, increasedCount: increased };
  }, [cards, search, typeFilter, expansionFilter, rarityFilter, saleStatusFilter, quickFilter, needsReviewOnly]);

  return useMemo(() => ({
    search, setSearch,
    typeFilter, setTypeFilter,
    expansionFilter, setExpansionFilter, expansionOptions,
    rarityFilter, setRarityFilter, rarityOptions,
    saleStatusFilter, setSaleStatusFilter, saleStatusOptions, saleStatusCounts,
    quickFilter, setQuickFilter,
    needsReviewOnly, setNeedsReviewOnly, needsReviewCount,
    filtered, dropCount, increasedCount,
  }), [
    search, setSearch, typeFilter, setTypeFilter,
    expansionFilter, expansionOptions, rarityFilter, rarityOptions,
    saleStatusFilter, saleStatusOptions, saleStatusCounts,
    quickFilter, setQuickFilter, needsReviewOnly, needsReviewCount,
    filtered, dropCount, increasedCount,
  ]);
}

// ─── useCardSelection ─────────────────────────────────────────────────────────

/**
 * 가격 조회 화면의 공용 선택 — 지금 쓰는 것은 선택 id · 지우기(① 대상 · ③ 대상)뿐이다. 옛 카드 목록 WorkflowStatusBar 용 파생 건수
 * (선택 중 하락 · 상승 · 미조회 · 반영 완료)와 '검색 결과 전체 선택'(allFilteredSelected · selectAllFiltered)은 PR #89 · #90 뒤
 * 쓰는 곳이 없어 내보내지 않는다(감사 card-controltower#752). 재고 화면(StockPageContext)은 useListSelection 을 그대로 쓴다.
 */
function useCardSelection(filtered: Card[]) {
  const { selectedIds, setSelectedIds, selectedSet, rowSelection, clearSelection } = useListSelection(filtered);
  return useMemo(() => ({
    selectedIds, setSelectedIds, selectedSet, rowSelection, clearSelection,
  }), [selectedIds, setSelectedIds, selectedSet, rowSelection, clearSelection]);
}

// ─── useCardView (공개 API) ───────────────────────────────────────────────────

/**
 * 뷰 상태(필터·선택)를 담당하는 훅.
 * cards → filtered → selectedIds 파이프라인을 한 곳에서 관리한다.
 */
export function useCardView(cards: Card[]) {
  const filters   = useCardFilters(cards);
  const selection = useCardSelection(filters.filtered);

  return useMemo(() => ({ filters, selection }), [filters, selection]);
}